// /src/components/core/SmartLinkPreview.jsx
// Preview page shown to non-members when they visit a smart link
// Shows group/community details with a join button
// Theme-aware: blue for communities, mauve for groups

import React, { useState } from 'react';
import { tokens } from '../../styles/designTokens';
import { useTranslation } from '../../i18n/useTranslation';
import ErrorReportModal from './ErrorReportModal';

const SmartLinkPreview = ({
  visible = false,
  targetType = 'community', // 'community' or 'group'
  targetData = null,
  linkData = null,
  isLoggedIn = false,
  onJoin = null,
  onLogin = null,
  onClose = null,
  isLoading = false
}) => {
  const { t } = useTranslation('common');
  const [showErrorReport, setShowErrorReport] = useState(false);

  if (!visible || !targetData) return null;

  // Determine theme based on target type (sourced from the single design-token layer)
  const isGroup = targetType === 'group' || targetType === 'community';
  const primaryColor = isGroup ? tokens.colors.contextGroups : tokens.colors.contextLocality; // Mauve for groups, Blue for communities
  const displayType = targetType === 'community' ? t('community_label') : t('group_label');

  // Extract target details
  const {
    name = 'Unknown',
    description = '',
    thumbnail_url = null,
    is_private = false,
    member_count = 0
  } = targetData;

  // Join label: mirrors GroupPrivateGate.jsx's idiom — driven by auto_accept_members, not
  // is_private (the two are independent; see DATABASE.md § group_join_requests).
  const autoAccept = targetData.auto_accept_members !== false;

  // Check if link is expired or maxed out
  const linkExpired = linkData?.expires_at && new Date(linkData.expires_at) < new Date();
  const linkMaxedOut = linkData?.max_uses && linkData?.use_count >= linkData?.max_uses;
  const linkUnusable = !linkData?.is_usable || linkExpired || linkMaxedOut;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '20px'
    }}>
      {/* Backdrop */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)'
        }}
        onClick={onClose}
      />

      {/* Preview Card */}
      <div
        style={{
          position: 'relative',
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
          width: '100%',
          maxWidth: '500px',
          overflow: 'hidden',
          zIndex: 10000
        }}
      >
        {/* Header with thumbnail or placeholder */}
        <div style={{
          height: '200px',
          background: thumbnail_url
            ? `url(${thumbnail_url}) center/cover`
            : `linear-gradient(135deg, ${primaryColor}dd, ${primaryColor}99)`,
          position: 'relative'
        }}>
          {/* Close button */}
          {onClose && (
            <button
              onClick={onClose}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                color: '#1a1a1a',
                border: 'none',
                fontSize: '20px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
              }}
            >
              ×
            </button>
          )}

          {/* Private badge */}
          {is_private && (
            <div style={{
              position: 'absolute',
              top: '12px',
              left: '12px',
              padding: '6px 12px',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: '600'
            }}>
              🔒 {t('private_label')}
            </div>
          )}
        </div>

        {/* Content */}
        <div style={{ padding: '24px' }}>
          {/* Type badge */}
          <div style={{
            display: 'inline-block',
            padding: '4px 12px',
            backgroundColor: `${primaryColor}22`,
            color: primaryColor,
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '600',
            marginBottom: '12px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            {displayType}
          </div>

          {/* Name */}
          <h2 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#1a1a1a',
            marginBottom: '12px',
            lineHeight: '1.3'
          }}>
            {name}
          </h2>

          {/* Description */}
          {description && (
            <p style={{
              fontSize: '14px',
              color: '#666',
              lineHeight: '1.6',
              marginBottom: '16px',
              maxHeight: '100px',
              overflow: 'auto'
            }}>
              {description}
            </p>
          )}

          {/* Stats */}
          <div style={{
            display: 'flex',
            gap: '16px',
            marginBottom: '24px',
            paddingTop: '16px',
            borderTop: '1px solid #e5e7eb'
          }}>
            <div>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#1a1a1a' }}>
                {member_count}
              </div>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                {t('members_label')}
              </div>
            </div>
          </div>

          {/* Link status messages */}
          {linkUnusable && (
            <div style={{
              padding: '12px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              marginBottom: '16px'
            }}>
              <p style={{ fontSize: '14px', color: '#dc2626', margin: 0 }}>
                {linkExpired && `⏰ ${t('link_expired')}`}
                {linkMaxedOut && `🔒 ${t('link_maxed_out')}`}
                {!linkExpired && !linkMaxedOut && `❌ ${t('link_inactive')}`}
              </p>
            </div>
          )}

          {/* Action buttons — never offered on a federation (4C §5: federates groups, not
              people; a smart link is a generic surface this build didn't otherwise touch, so
              the same suppression the join/request-to-join control gets everywhere else
              applies here too — flagged by Advisor consultation on the 4C diff). */}
          {!linkUnusable && !targetData.is_federation && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {isLoggedIn ? (
                <>
                  {/* Join button for logged-in users */}
                  <button
                    onClick={onJoin}
                    disabled={isLoading}
                    style={{
                      width: '100%',
                      padding: '14px',
                      backgroundColor: isLoading ? '#ccc' : primaryColor,
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      transition: 'opacity 0.2s',
                      opacity: isLoading ? 0.6 : 1
                    }}
                    onMouseOver={(e) => !isLoading && (e.currentTarget.style.opacity = '0.9')}
                    onMouseOut={(e) => !isLoading && (e.currentTarget.style.opacity = '1')}
                  >
                    {isLoading
                      ? t('joining_loading')
                      : (autoAccept ? t('join_button', { displayType }) : t('groups:request_to_join'))}
                  </button>
                </>
              ) : (
                <>
                  {/* Login/Register prompt for non-logged-in users */}
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '8px',
                    marginBottom: '8px'
                  }}>
                    <p style={{
                      fontSize: '14px',
                      color: '#4b5563',
                      margin: 0,
                      textAlign: 'center'
                    }}>
                      {t('login_to_join_message', { displayType: displayType.toLowerCase() })}
                    </p>
                  </div>
                  <button
                    onClick={onLogin}
                    style={{
                      width: '100%',
                      padding: '14px',
                      backgroundColor: primaryColor,
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'opacity 0.2s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
                    onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    {t('login_register_button')}
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Help Button */}
        <button
          onClick={() => setShowErrorReport(true)}
          title={t('report_problem')}
          style={{
            position: 'absolute',
            bottom: '12px',
            right: '12px',
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            backgroundColor: '#F9A61B',
            color: 'white',
            border: 'none',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
          }}
        >
          ?
        </button>
      </div>

      {/* Error Report Modal */}
      <ErrorReportModal
        isOpen={showErrorReport}
        onClose={() => setShowErrorReport(false)}
        currentView={t('smart_link_preview_title')}
        currentContext={{
          name: name,
          type: targetType,
          component: 'SmartLinkPreview',
          linkSlug: linkData?.slug
        }}
      />
    </div>
  );
};

export default SmartLinkPreview;
